import * as React from 'react';
import { Link } from 'react-router-dom';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet } from '../lib/api';
import { PageTitle, Card, Kpi, Section, Pill, Btn, fmt } from '../components/ui-kit';
import { UserCheck } from 'lucide-react';

interface ExpertDash {
  missions?: number;
  cra_pending?: number;
  earnings?: number;
  next_payment_date?: string;
  proposals_open?: number;
}

export default function Dashboard() {
  const { getToken, principal } = useXyqarAuth();
  const [s, setS] = React.useState<ExpertDash | null>(null);
  const [err, setErr] = React.useState<string | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        const tok = await getToken().catch(() => '');
        const r = await apiGet<ExpertDash>('/api/v1/expert/dashboard', tok);
        setS(r);
      } catch (e: any) { setErr(e.message ?? 'unavailable'); setS({}); }
    })();
  }, [getToken]);

  const noProfile = err && /no expert profile/i.test(err);
  const name = principal?.email?.split('@')[0] ?? 'Expert';
  const profileState = noProfile ? 'Profile missing' : 'Profile active';

  return (
    <div>
      <PageTitle
        kicker="Expert cockpit"
        title={name}
        right={<Pill tone="live"><UserCheck className="h-3 w-3" /> wolf-id active</Pill>}
      />

      {noProfile && (
        <Card className="mb-6 border-amber-500/30 bg-amber-500/5">
          <p className="text-amber-300 text-sm">No expert profile is linked to this account yet.</p>
          <p className="text-white/60 text-xs mt-2">Complete your profile to enter blind matching, receive high-fit missions, and stop competing on generic CV noise.</p>
          <div className="mt-4">
            <Btn primary onClick={() => (window.location.href = '/profile')}>Complete my profile</Btn>
          </div>
        </Card>
      )}

      {!noProfile && err && (
        <Card className="mb-6 border-amber-500/30 bg-amber-500/5">
          <p className="text-amber-300 text-xs font-mono">Dashboard partial · {err}</p>
        </Card>
      )}

      <Section title="At a glance" hint="What is live on your expert account right now">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi accent="blue"  label="Active missions"  value={fmt(s?.missions)} />
          <Kpi accent="amber" label="Pending CRA"      value={fmt(s?.cra_pending)} />
          <Kpi accent="green" label="Earnings YTD €"   value={fmt(Math.round(s?.earnings ?? 0))} />
          <Kpi              label="Open proposals"   value={fmt(s?.proposals_open)} hint={s?.next_payment_date ? `Next payout · ${s.next_payment_date}` : undefined} />
        </div>
      </Section>

      <Section title="Action lanes" hint="Jump directly into the operating surfaces behind the metrics">
        <div className="flex flex-wrap gap-3">
          <Link to="/profile"><Btn primary>Profile</Btn></Link>
          <Link to="/missions"><Btn>Missions</Btn></Link>
          <Link to="/payments"><Btn>Payments</Btn></Link>
          <Link to="/referral"><Btn>Referral</Btn></Link>
          <Link to="/account"><Btn>Account</Btn></Link>
        </div>
      </Section>

      <Section title="Operating posture" hint="Use the dashboard to know where execution friction sits before it costs you time or cash">
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Readiness</p>
            <p className="mt-2 text-white text-sm">{profileState}</p>
            <p className="mt-1 text-xs text-white/50">{noProfile ? 'Complete profile details to enter blind matching.' : 'Your account can be surfaced into qualified matching.'}</p>
          </Card>
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Delivery discipline</p>
            <p className="mt-2 text-white text-sm">{fmt(s?.cra_pending)} CRA entries pending</p>
            <p className="mt-1 text-xs text-white/50">Keep CRA current to avoid approval and payout delays.</p>
          </Card>
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Cash timing</p>
            <p className="mt-2 text-white text-sm">Next payout {s?.next_payment_date ?? 'not scheduled yet'}</p>
            <p className="mt-1 text-xs text-white/50">Visible earnings YTD: {fmt(Math.round(s?.earnings ?? 0))} €</p>
          </Card>
        </div>
      </Section>

      <Section title="Execution checklist" hint="What this dashboard is meant to help you close each week">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">1. Stay discoverable</p>
            <p className="mt-2 text-sm text-white">Profile quality controls whether you enter the ranked blind shortlist at all.</p>
          </Card>
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">2. Keep missions moving</p>
            <p className="mt-2 text-sm text-white">Open missions and CRA from the dashboard when delivery rhythm slows down.</p>
          </Card>
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">3. Watch payments</p>
            <p className="mt-2 text-sm text-white">Payments are traceable from the dedicated ledger instead of disappearing into back-office email loops.</p>
          </Card>
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">4. Expand referral</p>
            <p className="mt-2 text-sm text-white">Referral is part of the same operating surface so repeat opportunities stay attributable.</p>
          </Card>
        </div>
      </Section>

      <Section title="Why Xyqar is better for experts" hint="How the platform protects signal, rate, and timing">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Anonymity</p>
            <p className="text-white text-sm mt-2">You are introduced through your Wolf-ID first. No name, email, or photo is exposed before qualified interest exists.</p>
          </Card>
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Fairness</p>
            <p className="text-white text-sm mt-2">Wolf Brain v3 ranks on fit and persists a fairness grade on every match, not on who marketed themselves best.</p>
          </Card>
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">RGPD</p>
            <p className="text-white text-sm mt-2">You keep control over your account with export and deletion rights available from the account surfaces.</p>
          </Card>
        </div>
      </Section>
    </div>
  );
}
